import React, { useState } from 'react';
import { Box, Typography, Stepper, Paper } from '@mui/material';
import './manualOnboarding.css';
import {
  JSONContainer,
  PolicyData,
} from '../../../../../components/AccountOnboarding/ManualAccountOnboarding/JSONContainer';
import { ROLE_TRUST_POLICY } from '../config';
import CustomButton from '../../../../../components/MasterComponent/Button/CustomButton';

interface CreateRoleProps {
  onComplete: () => void;
  onBack: () => void;
}

const CreateRole: React.FC<CreateRoleProps> = ({ onComplete, onBack }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});

  const [trustPolicies, setTrustPolicies] = useState<PolicyData[]>(
    ROLE_TRUST_POLICY
  );

  const handleCopyJson = (index: number) => {
    trustPolicies[index].json &&
      navigator.clipboard.writeText(trustPolicies[index].json || 'Copied').then(
        () => {
          const updatedPolicies = [...trustPolicies];
          updatedPolicies[index].copied = true;
          setTrustPolicies(updatedPolicies);

          // Update completion status for this step
          const newCompleted = { ...completed };
          newCompleted[index] = true;
          setCompleted(newCompleted);

          // Reset copied status after 3 seconds
          setTimeout(() => {
            const resetPolicies = [...updatedPolicies];
            resetPolicies[index].copied = false;
            setTrustPolicies(resetPolicies);
          }, 3000);
        },
        (err) => {
          console.error('Could not copy text: ', err);
        }
      );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const totalSteps = () => {
    return trustPolicies.length;
  };

  const allStepsCompleted = () => {
    return Object.keys(completed).length === totalSteps();
  };

  return (
    <Box className="createRoleContainer">
      <Paper elevation={0} className="instructionsPaper">
        <p className="instructions">
          Follow these steps to create a role in your AWS IAM console. Copy the
          trust policy JSON and create a new role with the suggested name.
          Attach the policies you created in the previous step to this role.
        </p>
      </Paper>

      <Box className="roleStepsContainer">
        <p className="roleStepTitle">
          Step 1: Create IAM Role with Trust Policy
        </p>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          className="roleStepper"
        >
          {trustPolicies.map((policy, index) => (
            <JSONContainer
              key={index}
              index={index}
              policy={policy}
              activeStep={activeStep}
              totalSteps={totalSteps()}
              onCopyJson={handleCopyJson}
              onBack={handleBack}
            />
          ))}
        </Stepper>

        {allStepsCompleted() && (
          <Box className="roleInstructionsBox mt-10">
            <p className="roleStepTitle">Step 2: Attach Policies to Role</p>
            <Paper elevation={0} className="policiesListPaper mt-10">
              <Typography variant="body2" className="roleInstructions">
                Attach the following policies to your newly created role:
              </Typography>
              <ul className="policiesList">
                <li>cfx-WriteAccessPolicy</li>
                <li>cfx-Resource-read-policy</li>
                <li>cfx-Invoice-policy</li>
                <li>cfx-CostExplorer-policy</li>
                <li>cfx-ResourceTagging-policy</li>
              </ul>
            </Paper>
          </Box>
        )}
        <Box className="navigationButtons">
          <CustomButton buttonType="outlined" onClickHandler={onBack}>
            Back
          </CustomButton>
          <div className="d-flex align-center">
            {!allStepsCompleted() && (
              <p className="warningMessageManualOnboarding mr-10">
                ( Create IAM role with the provided trust policy JSON and attach
                all required policies before proceeding. )
              </p>
            )}
            <CustomButton
              buttonType="contained"
              disabled={!allStepsCompleted()}
              onClickHandler={onComplete}
            >
              Next: Complete S3 Bucket
            </CustomButton>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateRole;
